<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Page Header -->
      <v-row class="mb-1">
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl" class="page-header-card">
            <v-card-title class="py-1 px-2">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">Edit Penalty</h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Modify penalty details and track resolution status
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Edit Toggle and Back Button -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Edit/Read-Only Toggle Button -->
                    <v-btn
                      :color="isEditMode ? 'warning' : 'primary'"
                      :outlined="!isEditMode"
                      @click="toggleEditMode"
                      class="mr-2"
                      small
                      :disabled="isLoading"
                    >
                      <v-icon left>{{
                        isEditMode ? "mdi-eye" : "mdi-pencil"
                      }}</v-icon>
                      {{ isEditMode ? "Read Only" : "Edit Mode" }}
                    </v-btn>

                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                      small
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Penalties
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <!-- Loading State -->
      <v-row v-if="isLoading">
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl" class="form-card">
            <v-card-text class="py-12 text-center">
              <health-loader message="Loading penalty..." />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Error State -->
      <v-row v-else-if="!penalty && !isLoading">
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl" class="form-card">
            <v-card-text class="py-12 text-center">
              <v-icon size="80" color="error">mdi-alert-circle</v-icon>
              <h3 class="text-h6 grey--text mt-4 mb-2">Penalty Not Found</h3>
              <p class="grey--text mb-4">
                The requested penalty could not be found or may have been
                deleted.
              </p>
              <v-btn color="primary" @click="$router.back()">
                <v-icon left>mdi-arrow-left</v-icon>
                Go Back
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Professional Form Card -->
      <div v-else>
        <v-row>
          <v-col cols="12">
            <v-card elevation="6" rounded="xxl" class="form-card">
              <!-- Form Header -->
              <v-card-title class="form-header py-1 px-2">
                <h2 class="form-title">Penalty Information</h2>
                <v-spacer></v-spacer>
                <v-chip
                  :color="isEditMode ? 'success' : 'warning'"
                  :outlined="!isEditMode"
                  x-small
                >
                  <v-icon left x-small>
                    {{ isEditMode ? "mdi-pencil" : "mdi-eye" }}
                  </v-icon>
                  {{ isEditMode ? "Edit Mode" : "Read Only Mode" }}
                </v-chip>
              </v-card-title>

              <v-divider></v-divider>

              <!-- Form Content -->
              <v-card-text class="form-content px-2 py-2">
                <v-form ref="form" v-model="valid" lazy-validation>
                  <!-- Basic Information Section -->
                  <div class="form-section mb-2">
                    <h3 class="section-title mb-1">
                      <v-icon color="primary" class="mr-2">mdi-gavel</v-icon>
                      Penalty Information
                    </h3>

                    <v-row>
                      <v-col cols="12" md="6">
                        <v-autocomplete
                          v-model="form.user_id"
                          :items="users"
                          item-text="name"
                          item-value="id"
                          label="Select User"
                          placeholder="Choose the user to penalize"
                          outlined
                          :disabled="!isEditMode"
                          :readonly="!isEditMode"
                          :error-messages="formErrors.user_id"
                          class="professional-field"
                          hide-details="auto"
                          clearable
                        >
                          <template v-slot:prepend-inner>
                            <v-icon color="grey">mdi-account</v-icon>
                          </template>
                        </v-autocomplete>
                      </v-col>

                      <v-col cols="12" md="6">
                        <v-autocomplete
                          v-model="form.penalty_type_id"
                          :items="penaltyTypes"
                          item-text="name"
                          item-value="id"
                          label="Penalty Type"
                          placeholder="Select penalty type"
                          outlined
                          :disabled="!isEditMode"
                          :readonly="!isEditMode"
                          :error-messages="formErrors.penalty_type_id"
                          class="professional-field"
                          hide-details="auto"
                          clearable
                        >
                          <template v-slot:prepend-inner>
                            <v-icon color="grey"
                              >mdi-format-list-bulleted</v-icon
                            >
                          </template>
                        </v-autocomplete>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="12" md="6">
                        <v-select
                          v-model="form.status"
                          :items="statusOptions"
                          label="Status"
                          placeholder="Select penalty status"
                          outlined
                          :disabled="!isEditMode"
                          :readonly="!isEditMode"
                          :error-messages="formErrors.status"
                          class="professional-field"
                          hide-details="auto"
                        >
                          <template v-slot:prepend-inner>
                            <v-icon color="grey">mdi-flag</v-icon>
                          </template>
                        </v-select>
                      </v-col>

                      <v-col cols="12" md="6">
                        <v-autocomplete
                          v-model="form.issued_by_id"
                          :items="users"
                          item-text="name"
                          item-value="id"
                          label="Issued By"
                          placeholder="Select who issued the penalty"
                          outlined
                          :disabled="!isEditMode"
                          :readonly="!isEditMode"
                          :error-messages="formErrors.issued_by_id"
                          class="professional-field"
                          hide-details="auto"
                          clearable
                        >
                          <template v-slot:prepend-inner>
                            <v-icon color="grey">mdi-account-tie</v-icon>
                          </template>
                        </v-autocomplete>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="12">
                        <v-textarea
                          v-model="form.remark"
                          label="Penalty Remarks"
                          placeholder="Enter detailed reason for the penalty"
                          outlined
                          rows="4"
                          :disabled="!isEditMode"
                          :readonly="!isEditMode"
                          :rules="[
                            (v) => !!v || 'Penalty remarks are required',
                          ]"
                          :error-messages="formErrors.remark"
                          class="professional-field"
                          hide-details="auto"
                          required
                        >
                          <template v-slot:prepend-inner>
                            <v-icon color="grey">mdi-text</v-icon>
                          </template>
                        </v-textarea>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Penalty Type Specific Section -->
                  <div class="form-section mb-2">
                    <h3 class="section-title mb-1">
                      <v-icon color="primary" class="mr-2"
                        >mdi-calendar-clock</v-icon
                      >
                      Penalty Details
                    </h3>

                    <!-- Penalty Type Selection (Read-only display when not editing) -->
                    <v-row v-if="!isEditMode">
                      <v-col cols="12">
                        <div class="d-flex align-center mb-3">
                          <v-chip
                            v-if="penalty && penalty.ban_from && penalty.ban_to"
                            color="error"
                            small
                          >
                            <v-icon left small>mdi-gavel</v-icon>
                            Ban/Suspension
                          </v-chip>
                          <v-chip
                            v-else-if="penalty && penalty.fine_amount"
                            color="warning"
                            small
                          >
                            <v-icon left small>mdi-currency-inr</v-icon>
                            Fine/Monetary
                          </v-chip>
                          <v-chip v-else color="info" small>
                            <v-icon left small>mdi-alert</v-icon>
                            Warning/Other
                          </v-chip>
                        </div>
                      </v-col>
                    </v-row>

                    <!-- Editable penalty type selection -->
                    <v-row v-if="isEditMode">
                      <v-col cols="12">
                        <v-radio-group
                          v-model="penaltyCategory"
                          row
                          class="mt-0"
                          hide-details
                        >
                          <v-radio
                            label="Ban/Suspension"
                            value="ban"
                            color="error"
                          ></v-radio>
                          <v-radio
                            label="Fine/Monetary"
                            value="fine"
                            color="warning"
                          ></v-radio>
                          <v-radio
                            label="Warning Only"
                            value="warning"
                            color="info"
                          ></v-radio>
                        </v-radio-group>
                      </v-col>
                    </v-row>

                    <!-- Ban Details -->
                    <v-row
                      v-if="
                        (isEditMode && penaltyCategory === 'ban') ||
                        (!isEditMode &&
                          penalty &&
                          penalty.ban_from &&
                          penalty.ban_to)
                      "
                    >
                      <v-col cols="12" md="6">
                        <v-menu
                          v-if="isEditMode"
                          v-model="banFromMenu"
                          :close-on-content-click="false"
                          :nudge-right="40"
                          transition="scale-transition"
                          offset-y
                          min-width="auto"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="form.ban_from"
                              label="Ban Start Date"
                              placeholder="Select start date"
                              outlined
                              readonly
                              v-bind="attrs"
                              v-on="on"
                              :error-messages="formErrors.ban_from"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template v-slot:prepend-inner>
                                <v-icon color="grey">mdi-calendar-start</v-icon>
                              </template>
                            </v-text-field>
                          </template>
                          <v-date-picker
                            v-model="form.ban_from"
                            @input="banFromMenu = false"
                          ></v-date-picker>
                        </v-menu>
                        <v-text-field
                          v-else
                          v-model="form.ban_from"
                          label="Ban Start Date"
                          outlined
                          readonly
                          class="professional-field"
                          hide-details="auto"
                        >
                          <template v-slot:prepend-inner>
                            <v-icon color="grey">mdi-calendar-start</v-icon>
                          </template>
                        </v-text-field>
                      </v-col>

                      <v-col cols="12" md="6">
                        <v-menu
                          v-if="isEditMode"
                          v-model="banToMenu"
                          :close-on-content-click="false"
                          :nudge-right="40"
                          transition="scale-transition"
                          offset-y
                          min-width="auto"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="form.ban_to"
                              label="Ban End Date"
                              placeholder="Select end date"
                              outlined
                              readonly
                              v-bind="attrs"
                              v-on="on"
                              :error-messages="formErrors.ban_to"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template v-slot:prepend-inner>
                                <v-icon color="grey">mdi-calendar-end</v-icon>
                              </template>
                            </v-text-field>
                          </template>
                          <v-date-picker
                            v-model="form.ban_to"
                            @input="banToMenu = false"
                          ></v-date-picker>
                        </v-menu>
                        <v-text-field
                          v-else
                          v-model="form.ban_to"
                          label="Ban End Date"
                          outlined
                          readonly
                          class="professional-field"
                          hide-details="auto"
                        >
                          <template v-slot:prepend-inner>
                            <v-icon color="grey">mdi-calendar-end</v-icon>
                          </template>
                        </v-text-field>
                      </v-col>
                    </v-row>

                    <!-- Fine Details -->
                    <v-row
                      v-if="
                        (isEditMode && penaltyCategory === 'fine') ||
                        (!isEditMode && penalty && penalty.fine_amount)
                      "
                    >
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="form.fine_amount"
                          label="Fine Amount"
                          placeholder="Enter fine amount"
                          outlined
                          type="number"
                          step="0.01"
                          min="0"
                          :disabled="!isEditMode"
                          :readonly="!isEditMode"
                          :rules="[
                            (v) =>
                              !v ||
                              parseFloat(v) >= 0 ||
                              'Fine amount must be 0 or greater',
                          ]"
                          :error-messages="formErrors.fine_amount"
                          class="professional-field"
                          hide-details="auto"
                        >
                          <template v-slot:prepend-inner>
                            <v-icon color="grey">mdi-currency-inr</v-icon>
                          </template>
                        </v-text-field>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Additional Information Section -->
                  <div class="form-section mb-2">
                    <h3 class="section-title mb-1">
                      <v-icon color="primary" class="mr-2"
                        >mdi-information</v-icon
                      >
                      Additional Information
                    </h3>

                    <v-row>
                      <v-col cols="12" md="6">
                        <v-menu
                          v-if="isEditMode"
                          v-model="resolvedAtMenu"
                          :close-on-content-click="false"
                          :nudge-right="40"
                          transition="scale-transition"
                          offset-y
                          min-width="auto"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="form.resolved_at"
                              label="Resolved At (Optional)"
                              placeholder="Select resolution date"
                              outlined
                              readonly
                              v-bind="attrs"
                              v-on="on"
                              :error-messages="formErrors.resolved_at"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template v-slot:prepend-inner>
                                <v-icon color="grey">mdi-calendar-check</v-icon>
                              </template>
                            </v-text-field>
                          </template>
                          <v-date-picker
                            v-model="form.resolved_at"
                            @input="resolvedAtMenu = false"
                          ></v-date-picker>
                        </v-menu>
                        <v-text-field
                          v-else
                          v-model="form.resolved_at"
                          label="Resolved At"
                          outlined
                          readonly
                          class="professional-field"
                          hide-details="auto"
                        >
                          <template v-slot:prepend-inner>
                            <v-icon color="grey">mdi-calendar-check</v-icon>
                          </template>
                        </v-text-field>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Status Section -->
                  <div class="form-section mb-2">
                    <h3 class="section-title mb-1">
                      <v-icon color="primary" class="mr-2">mdi-cog</v-icon>
                      Penalty Status
                    </h3>

                    <v-row>
                      <v-col cols="12" md="6">
                        <v-card outlined class="status-card">
                          <v-card-text class="pa-2">
                            <div class="d-flex align-center">
                              <v-icon color="grey" class="mr-3"
                                >mdi-check-circle</v-icon
                              >
                              <div class="flex-grow-1">
                                <div class="font-weight-medium mb-1">
                                  Active Status
                                </div>
                                <div class="text-caption grey--text">
                                  Whether this penalty is currently active
                                </div>
                              </div>
                              <v-switch
                                v-model="form.is_active"
                                color="success"
                                inset
                                hide-details
                                class="ml-3"
                                :disabled="!isEditMode"
                              ></v-switch>
                            </div>
                            <div class="mt-2">
                              <v-chip
                                small
                                :color="form.is_active ? 'success' : 'warning'"
                                :text-color="form.is_active ? 'white' : 'black'"
                              >
                                {{ form.is_active ? "Active" : "Inactive" }}
                              </v-chip>
                            </div>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                  </div>
                </v-form>
              </v-card-text>

              <!-- Form Actions Footer -->
              <v-divider></v-divider>
              <v-card-actions class="form-actions pa-2" v-if="isEditMode">
                <v-row class="w-100 ma-0" align="center">
                  <v-col cols="12" md="8" class="pa-0">
                    <div class="d-flex align-center">
                      <v-chip
                        small
                        :color="hasValidationErrors() ? 'error' : 'success'"
                        :text-color="hasValidationErrors() ? 'white' : 'white'"
                        class="mr-3"
                      >
                        <v-icon left x-small>
                          {{
                            hasValidationErrors()
                              ? "mdi-alert-circle"
                              : "mdi-check-circle"
                          }}
                        </v-icon>
                        {{
                          hasValidationErrors()
                            ? "Please fix validation errors"
                            : "Form validation passed"
                        }}
                      </v-chip>
                      <span class="text-caption grey--text">
                        Click save to update penalty information
                      </span>
                    </div>
                  </v-col>

                  <v-col cols="12" md="4" class="pa-0">
                    <div class="d-flex justify-end">
                      <v-btn
                        color="error"
                        outlined
                        class="mr-3 action-button"
                        @click="deletePenalty"
                        :disabled="isSaving || isDeleting"
                      >
                        <v-icon left>mdi-delete</v-icon>
                        Delete
                      </v-btn>
                      <v-btn
                        color="primary"
                        class="action-button"
                        @click="updatePenalty"
                        :loading="isSaving"
                        :disabled="isSaving || !valid"
                      >
                        <v-icon left>mdi-content-save</v-icon>
                        Save Changes
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </v-container>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400" persistent>
      <v-card>
        <v-card-title class="headline">
          <v-icon color="error" class="mr-2">mdi-delete</v-icon>
          Confirm Delete
        </v-card-title>
        <v-card-text>
          Are you sure you want to delete this penalty? This action can be
          undone later.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="grey" @click="deleteDialog = false">Cancel</v-btn>
          <v-btn
            color="error"
            @click="confirmDelete"
            :loading="isDeleting"
            text
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "PenaltyEditPage",
  data() {
    return {
      valid: false,
      penalty: null,
      form: {
        user_id: null,
        penalty_type_id: null,
        ban_from: null,
        ban_to: null,
        fine_amount: "",
        remark: "",
        status: 0,
        issued_by_id: null,
        resolved_at: null,
        is_active: true,
      },
      formErrors: {},
      isLoading: true,
      isSaving: false,
      isDeleting: false,
      isEditMode: false,
      deleteDialog: false,
      users: [],
      penaltyTypes: [],
      penaltyCategory: "warning",
      banFromMenu: false,
      banToMenu: false,
      resolvedAtMenu: false,
      statusOptions: [
        { text: "Pending", value: 0 },
        { text: "In Process", value: 1 },
        { text: "Completed", value: 2 },
        { text: "Rejected", value: 3 },
      ],
    };
  },

  computed: {
    hasValidationErrors() {
      return Object.keys(this.formErrors).length > 0 || !this.valid;
    },
  },

  watch: {
    penaltyCategory(newValue) {
      // Clear values when changing penalty category in edit mode
      if (newValue !== "ban") {
        this.form.ban_from = null;
        this.form.ban_to = null;
      }
      if (newValue !== "fine") {
        this.form.fine_amount = "";
      }
    },

    // Clear errors when user starts typing
    "form.user_id"() {
      if (this.formErrors.user_id) {
        this.$delete(this.formErrors, "user_id");
      }
    },
    "form.remark"() {
      if (this.formErrors.remark) {
        this.$delete(this.formErrors, "remark");
      }
    },
  },

  mounted() {
    this.getPenalty();
    this.getMasters();
  },

  methods: {
    async getPenalty() {
      this.isLoading = true;
      try {
        const penaltyId = this.$route.params.id;
        const response = await axios.get(`/penalties/${penaltyId}`);

        if (response.data.success) {
          this.penalty = response.data.data;
          this.populateForm();
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Penalty not found",
          });
          this.penalty = null;
        }
      } catch (error) {
        console.error(error);
        this.$store.dispatch("errors/setError", {
          message: "Failed to load penalty",
        });
        this.penalty = null;
      } finally {
        this.isLoading = false;
      }
    },

    populateForm() {
      if (this.penalty) {
        this.form = {
          user_id: this.penalty.user_id,
          penalty_type_id: this.penalty.penalty_type_id,
          ban_from: this.penalty.ban_from
            ? this.penalty.ban_from.split("T")[0]
            : null,
          ban_to: this.penalty.ban_to
            ? this.penalty.ban_to.split("T")[0]
            : null,
          fine_amount: this.penalty.fine_amount || "",
          remark: this.penalty.remark || "",
          status: this.penalty.status,
          issued_by_id: this.penalty.issued_by_id,
          resolved_at: this.penalty.resolved_at
            ? this.penalty.resolved_at.split("T")[0]
            : null,
          is_active: this.penalty.is_active,
        };

        // Set penalty category based on existing data
        if (this.penalty.ban_from && this.penalty.ban_to) {
          this.penaltyCategory = "ban";
        } else if (this.penalty.fine_amount) {
          this.penaltyCategory = "fine";
        } else {
          this.penaltyCategory = "warning";
        }
      }
    },

    toggleEditMode() {
      this.isEditMode = !this.isEditMode;
      if (!this.isEditMode) {
        // Reset form when going back to read-only mode
        this.populateForm();
        this.formErrors = {};
      }
    },

    async updatePenalty() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.isSaving = true;
      this.formErrors = {};

      try {
        // Prepare the data
        const penaltyData = {
          user_id: this.form.user_id,
          penalty_type_id: this.form.penalty_type_id,
          ban_from: this.penaltyCategory === "ban" ? this.form.ban_from : null,
          ban_to: this.penaltyCategory === "ban" ? this.form.ban_to : null,
          fine_amount:
            this.penaltyCategory === "fine" && this.form.fine_amount
              ? parseFloat(this.form.fine_amount)
              : null,
          remark: this.form.remark,
          status: this.form.status,
          issued_by_id: this.form.issued_by_id,
          resolved_at: this.form.resolved_at,
          is_active: this.form.is_active,
        };

        const penaltyId = this.$route.params.id;
        const response = await axios.put(
          `/penalties/${penaltyId}`,
          penaltyData
        );

        if (response.data.success) {
          this.penalty = response.data.data;
          this.populateForm();
          this.isEditMode = false;
          this.$store.dispatch("errors/setError", {
            message: "Penalty updated successfully",
            type: "success",
          });
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              response.data.message ||
              "An error occurred while updating the penalty.",
          });
        }
      } catch (error) {
        console.error(error);

        if (error.response && error.response.status === 422) {
          // Validation errors
          this.formErrors = error.response.data.errors || {};
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Failed to update penalty",
          });
        }
      } finally {
        this.isSaving = false;
      }
    },

    deletePenalty() {
      this.deleteDialog = true;
    },

    async confirmDelete() {
      this.isDeleting = true;
      try {
        const penaltyId = this.$route.params.id;
        const response = await axios.post(`/penalties/delete/${penaltyId}`);

        if (response.data.success) {
          this.$store.dispatch("errors/setError", {
            message: "Penalty deleted successfully",
            type: "success",
          });
          this.$router.push("/penalties");
        }
      } catch (error) {
        this.$store.dispatch("errors/setError", {
          message: "Failed to delete penalty",
        });
      } finally {
        this.isDeleting = false;
        this.deleteDialog = false;
      }
    },

    async getMasters() {
      try {
        const response = await axios.get("masters?context=penalties");
        const masters = response.data;

        // Users
        if (masters.users) {
          this.users = masters.users;
        }

        // Penalty Types (from ValueList - use description field)
        if (masters.penalty_types) {
          this.penaltyTypes = masters.penalty_types.map(penaltyType => ({
            id: penaltyType.id,
            name: penaltyType.description,
            value: penaltyType.id,
            text: penaltyType.description
          }));
        }

        // Positions (if needed)
        if (masters.positions) {
          this.positions = masters.positions;
        }
      } catch (error) {
        console.log("Error loading masters data:", error);
      }
    },
  },
};
</script>

<style scoped>
/* Use the same styling as users edit page */
.page-header-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.form-card {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  border-left: 4px solid var(--v-primary-base);
}

.form-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.form-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--v-primary-base);
}

.back-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

.form-content {
  background: #ffffff;
}

.form-section {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 0.5rem;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.professional-field >>> .v-input__control {
  min-height: 40px;
}

.professional-field >>> .v-text-field__details {
  padding-top: 4px;
}

.professional-field >>> .v-input__slot {
  border-radius: 4px;
}

.professional-field >>> .v-text-field input {
  font-size: 0.75rem;
  padding-top: 8px;
  padding-bottom: 8px;
}

.professional-field >>> .v-label {
  font-size: 0.75rem;
  font-weight: 500;
}

.professional-field >>> .v-icon {
  color: #6c757d !important;
}

.professional-field >>> .v-textarea textarea {
  font-size: 0.75rem;
  line-height: 1.4;
}

.status-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.status-card:hover {
  border-color: var(--v-primary-base);
  background-color: #f8f9fa;
}

.form-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid #dee2e6;
}

.action-button {
  min-width: 90px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  border-radius: 4px;
  font-size: 0.75rem;
}

.v-row {
  margin-left: -6px;
  margin-right: -6px;
}

.v-row > .v-col {
  padding: 0 6px;
}

.v-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
}

.v-btn.v-btn--outlined {
  border: 2px solid currentColor;
}

.v-chip {
  font-weight: 500;
}

@media (max-width: 960px) {
  .page-title {
    font-size: 1.1rem;
  }

  .section-title {
    font-size: 0.85rem;
  }

  .form-content {
    padding: 0.75rem !important;
  }

  .back-button,
  .action-button {
    min-width: 80px;
    font-size: 0.7rem;
  }
}

.professional-field >>> .v-input--is-focused .v-input__slot {
  border-color: var(--v-primary-base) !important;
  border-width: 2px !important;
}

.mb-1 {
  margin-bottom: 0.5rem !important;
}

.mb-2 {
  margin-bottom: 0.75rem !important;
}
</style>